import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { Loader2, Play, AlertCircle } from "lucide-react";
import { WorkflowData } from "../WorkflowEditor";

interface TestWorkflowModalProps {
  open: boolean;
  onClose: () => void;
  workflow: WorkflowData;
}

export function TestWorkflowModal({ open, onClose, workflow }: TestWorkflowModalProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [testing, setTesting] = useState(false);
  const [unaId, setUnaId] = useState<string>("");
  const [unas, setUnas] = useState<any[]>([]);
  const [loadingUnas, setLoadingUnas] = useState(false);

  // Load user's UNAs when modal opens
  useState(() => {
    if (open) {
      loadUnas();
    }
  });

  async function loadUnas() {
    setLoadingUnas(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get UNAs where user is treasury secretary or member
      const { data: treasuryUnas } = await supabase
        .from('unas')
        .select('id, name')
        .eq('treasury_secretary_id', user.id);

      const { data: memberUnas } = await supabase
        .from('una_members')
        .select('una_id, unas(id, name)')
        .eq('user_id', user.id)
        .eq('status', 'active');

      const allUnas = [
        ...(treasuryUnas || []),
        ...(memberUnas?.map(m => m.unas).filter(Boolean) || []),
      ];

      // Deduplicate
      const uniqueUnas = Array.from(
        new Map(allUnas.map(u => [u.id, u])).values()
      );

      setUnas(uniqueUnas);
      if (uniqueUnas.length > 0) {
        setUnaId(uniqueUnas[0].id);
      }
    } catch (error) {
      console.error('Error loading UNAs:', error);
    } finally {
      setLoadingUnas(false);
    }
  }

  async function handleTest() {
    if (!unaId) {
      toast({
        title: "Error",
        description: "Please select a UNA for testing",
        variant: "destructive",
      });
      return;
    }

    if (!workflow.id) {
      toast({
        title: "Error",
        description: "Please save the workflow before testing",
        variant: "destructive",
      });
      return;
    }

    setTesting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Create test task
      const { data: task, error: taskError } = await supabase
        .from('custom_tasks')
        .insert({
          task_title: `[TEST] ${workflow.title}`,
          task_description: `Testing workflow: ${workflow.description}`,
          una_id: unaId,
          user_id: user.id,
          status: 'in_progress',
          is_from_workflow: true,
          workflow_id: workflow.id,
          category: workflow.category,
        })
        .select()
        .single();

      if (taskError) throw taskError;

      // Create task_workflows entry
      const { error: workflowError } = await supabase
        .from('task_workflows')
        .insert({
          task_id: task.id,
          workflow_id: workflow.id,
          una_id: unaId,
          current_phase: 1,
          workflow_data: {
            workflow_key: workflow.workflow_key,
            title: workflow.title,
            phases: workflow.phases,
          },
        });

      if (workflowError) throw workflowError;

      // Create task phases
      if (workflow.phases && workflow.phases.length > 0) {
        const phaseInserts = workflow.phases.map((phase: any, index: number) => ({
          task_id: task.id,
          phase_number: index + 1,
          phase_name: phase.phase_name || `Phase ${index + 1}`,
          description: phase.description || '',
          status: index === 0 ? 'in_progress' : 'not_started',
          estimated_time: phase.estimated_time || '',
          tasks_checklist: phase.tasks || [],
        }));

        const { error: phasesError } = await supabase
          .from('task_phases')
          .insert(phaseInserts);

        if (phasesError) throw phasesError;
      }

      toast({
        title: "Test Workflow Created",
        description: "Opening test task for you to complete...",
      });

      onClose();
      navigate(`/tasks/${task.id}`);
    } catch (error: any) {
      console.error('Error creating test workflow:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create test workflow",
        variant: "destructive",
      });
    } finally {
      setTesting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Test Workflow</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This will create a real task in your selected UNA. Complete the workflow end-to-end to test all phases, documents, and completion flow.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label>Select UNA for Testing</Label>
            {loadingUnas ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading UNAs...
              </div>
            ) : unas.length === 0 ? (
              <Alert variant="destructive">
                <AlertDescription>
                  No UNAs found. You need at least one UNA to test workflows.
                </AlertDescription>
              </Alert>
            ) : (
              <Select value={unaId} onValueChange={setUnaId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a UNA" />
                </SelectTrigger>
                <SelectContent>
                  {unas.map((una) => (
                    <SelectItem key={una.id} value={una.id}>
                      {una.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="text-sm text-muted-foreground space-y-1">
            <p>Test workflow will:</p>
            <ul className="list-disc list-inside pl-2 space-y-1">
              <li>Create a task marked as [TEST]</li>
              <li>Set up all phases from workflow</li>
              <li>Allow you to generate documents</li>
              <li>Let you complete the full workflow</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={testing}>
            Cancel
          </Button>
          <Button onClick={handleTest} disabled={testing || unas.length === 0}>
            {testing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating Test...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Launch Test
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
